export const EmptyGuid = "00000000-0000-0000-0000-000000000000";

export type HydrationOption = {
  Text: string;
  Value: string;
};

export type ConsiderationSelection = {
  Key?: string;
  Text?: string;
  Value?: string | string[];
  Name?: string;
};

export type HydrationFieldType =
  | "text"
  | "numeric"
  | "multiselect"
  | "dropdown"
  | "hidden"
  | "boolean"
  | "content";

export type HydrationFieldConfig<TResponse extends Record<string, unknown>> = {
  property: keyof TResponse;
  selector: string;
  type: HydrationFieldType;
  fallback?: string | number | string[] | boolean;
  required?: boolean;
  transform?: (
    value: TResponse[keyof TResponse] | undefined,
    response: TResponse,
  ) => string | number | string[] | boolean;
  getOptions?: (
    value: TResponse[keyof TResponse] | undefined,
    response: TResponse,
  ) => HydrationOption[];
};

export type HydratorConfig<TResponse extends Record<string, unknown>> = {
  fields: HydrationFieldConfig<TResponse>[];
  afterHydrate?: (response: TResponse) => void;
  getForm: () => HTMLFormElement | null;
  replaceForm: (markup: string) => void;
  formId: string;
};

export type TaskGlobalHandlers = {
  saveTask: () => void;
  calculateRisk: () => void;
  selectTask: (element: HTMLButtonElement) => void;
  deleteTask: (key: string) => void;
  addNewTask: () => void;
  taskDataBounded: () => void;
  refreshTasksRecords: () => void;
  getSelectedProjectPhases: () => { projectPhases: string[] };
};

export type FormGlobalHandlers = {
  formValueChange: (e: any) => void;
  initializeForm: (rowKey: string) => Promise<void>;
  handleOtherValueChange: (e: any) => void;
  onRefreshFormsClick: (e: any) => void;
  onSafetyFormSubmit: (e: any) => void;
};

export type TaskResponse = {
  Key: string;
  New: boolean;
  Color: string;
  Active: boolean;
  Name: string;
  IsField: boolean;
  IsInHouse: boolean;
  ProcessSafetyHazards: string;
  ProcessRisk: string;
  ProcessLikelihood: number;
  ProcessSeverity: number;
  ProcessRiskScore: number;
  ProcessControlsOrMitigations: string;
  PersonalRisk: string;
  PersonalLikelihood: number;
  PersonalSeverity: number;
  PersonalRiskScore: number;
  PersonalControlsOrMitigations: string;
  PersonalSafetyHazards: string;
  ProcessConsiderations: Array<string | ConsiderationSelection>;
  PersonalConsiderations: Array<string | ConsiderationSelection>;
  ProjectPhases: string[];
};

export type Consideration = {
  New?: boolean;
  Key: string;
  Name: string;
  Active: boolean;
  Tooltip?: string;
  Types: string[];
};

export type ConsiderationOption = {
  Key: string;
  Name: string;
  FormKey?: string;
  ConsiderationKey: string;
  /** Sort hint, lowest first; ties fall back to Name. 0 leaves the set alphabetical. */
  SortOrder: number;
  /** Help text shown under this option's label when it is chosen on a JSA. */
  Description: string;
  /** Releasing a JSA carrying this option asks the safety recipient to acknowledge it. */
  NotifySafety: boolean;
  RiskSeverityAdder: number;
  RiskLikelihoodAdder: number;
  SafetyHazards: string;
  Risk: string;
  ControlsOrMitigations: string;
  QualificationAndRequirements: string[];
};

export type ConsiderationOptionDataItem = {
  Key?: string;
  Value?: string;
  Name?: string;
  ConsiderationKey?: string;
  RiskSeverityAdder?: number;
  RiskLikelihoodAdder?: number;
  ProcessSafetyHazards?: string;
  PersonalSafetyHazard?: string;
  Risk?: string;
  ControlsOrMitigations?: string;
  Group?: {
    Name?: string;
  };
};
export const LogLevel = {
  Trace: 0,
  Debug: 1,
  Info: 2,
  Warn: 3,
  Error: 4,
  Crititcal: 5,
  None: 6
} as const;

export type LogLevel = (typeof LogLevel)[keyof typeof LogLevel];

export type LogRequest = {
  level: LogLevel;
  message: string;
}
